import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { 
  AlertTriangle, 
  GitBranch, 
  Eye, 
  Edit, 
  Trash2, 
  Clock,
  CheckCircle,

  ArrowRight,
  Calendar
} from 'lucide-react';
import { AppDispatch } from '../../../store';
import { 
  updateDependencyStatus, 
  deleteDependency,
  fetchDependencies,
  fetchAtRiskDependencies 
} from '../../../store/slices/dependenciesSlice';
import Button from '../../../components/ui/Button';
import Badge from '../../../components/ui/Badge';
import DataTable from '../../../components/ui/DataTable';
import { GlassModal } from '@/design-system';
import { DependencyForm } from './DependencyForm';
import { DependencyDetailModal } from './DependencyDetailModal';
import { 
  Dependency, 
  DependencyType, 
  DependencyStatus, 
  DependencySeverity,
  Column 
} from '../../../types';

interface DependencyListProps {
  dependencies: Dependency[];
  viewMode: 'all' | 'at-risk';
}

export const DependencyList: React.FC<DependencyListProps> = ({ 
  dependencies, 
  viewMode 
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedDependency, setSelectedDependency] = useState<Dependency | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const getSeverityColor = (severity: DependencySeverity) => {
    switch (severity) {
      case DependencySeverity.CRITICAL:
        return 'danger';
      case DependencySeverity.HIGH:
        return 'warning';
      case DependencySeverity.MEDIUM:
        return 'info';
      case DependencySeverity.LOW:
        return 'success';
      default:
        return 'secondary';
    }
  };

  const getStatusColor = (status: DependencyStatus) => {
    switch (status) {
      case DependencyStatus.OPEN:
        return 'danger';
      case DependencyStatus.IN_PROGRESS:
        return 'info';
      case DependencyStatus.RESOLVED:
        return 'success';
      case DependencyStatus.CANCELLED:
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getTypeIcon = (type: DependencyType) => {
    switch (type) {
      case DependencyType.BLOCKING:
        return <AlertTriangle className="w-4 h-4" />;
      case DependencyType.ENABLING:
        return <GitBranch className="w-4 h-4" />;
      case DependencyType.INFORMATIONAL:
        return <Eye className="w-4 h-4" />;
      default:
        return <GitBranch className="w-4 h-4" />;
    }
  };

  const formatEntityType = (entityType: string) => {
    return entityType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  const getRiskIndicator = (dependency: Dependency) => {
    if (dependency.riskLevel === 'OVERDUE') {
      return (
        <Badge variant="danger" className="flex items-center space-x-1">
          <Clock className="w-3 h-3" />
          <span>Overdue</span>
        </Badge>
      );
    }
    if (dependency.riskLevel === 'DUE_SOON') {
      return (
        <Badge variant="warning" className="flex items-center space-x-1">
          <Clock className="w-3 h-3" />
          <span>Due Soon</span>
        </Badge>
      );
    }
    return null;
  };

  const handleStatusChange = async (dependency: Dependency, newStatus: DependencyStatus) => {
    try {
      await dispatch(updateDependencyStatus({
        id: dependency.id,
        status: newStatus,
        resolvedDate: newStatus === DependencyStatus.RESOLVED ? new Date().toISOString() : undefined
      })).unwrap();
      
      // Refresh the list
      if (viewMode === 'all') {
        dispatch(fetchDependencies());
      } else {
        dispatch(fetchAtRiskDependencies());
      }
    } catch (error) {
      console.error('Failed to update dependency status:', error);
    }
  };

  const handleDelete = async () => {
    if (!selectedDependency) return;
    
    try {
      await dispatch(deleteDependency(selectedDependency.id)).unwrap();
      setShowDeleteConfirm(false);
      setSelectedDependency(null);
      
      // Refresh the list
      if (viewMode === 'all') {
        dispatch(fetchDependencies());
      } else {
        dispatch(fetchAtRiskDependencies());
      }
    } catch (error) {
      console.error('Failed to delete dependency:', error);
    }
  };

  const columns: Column<Dependency>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (value, dependency) => (
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            {getTypeIcon(dependency.type)}
          </div>
          <div>
            <div className="font-medium text-foreground">{value}</div>
            {dependency.description && (
              <div className="text-sm text-muted-foreground truncate max-w-xs">
                {dependency.description}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'relationship',
      header: 'Relationship',
      render: (_, dependency) => (
        <div className="flex items-center space-x-2 text-sm">
          <div className="text-foreground">
            <div className="font-medium">{dependency.sourceName || 'Unknown'}</div>
            <div className="text-xs text-muted-foreground">
              {formatEntityType(dependency.sourceType)}
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground" />
          <div className="text-foreground">
            <div className="font-medium">{dependency.targetName || 'Unknown'}</div>
            <div className="text-xs text-muted-foreground">
              {formatEntityType(dependency.targetType)}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      render: (value) => (
        <Badge variant="secondary" className="flex items-center space-x-1">
          {getTypeIcon(value)}
          <span>{value}</span>
        </Badge>
      ),
    },
    {
      key: 'severity',
      header: 'Severity',
      sortable: true,
      render: (value) => (
        <Badge variant={getSeverityColor(value)}>
          {value}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (value, dependency) => (
        <div className="flex items-center space-x-2">
          <Badge variant={getStatusColor(value)}>
            {value.replace('_', ' ')}
          </Badge>
          {getRiskIndicator(dependency)}
        </div>
      ),
    },
    {
      key: 'expectedDate',
      header: 'Due Date',
      sortable: true,
      render: (value, dependency) => {
        if (!value) return <span className="text-muted-foreground">No date set</span>;
        
        const date = new Date(value);
        const now = new Date();
        const isOverdue = date < now;
        const daysUntilDue = Math.ceil((date.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
        
        return (
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <div>
              <div className={`text-sm ${isOverdue ? 'text-status-error' : 'text-foreground'}`}>
                {date.toLocaleDateString()}
              </div>
              {dependency.daysUntilDue !== null && (
                <div className="text-xs text-muted-foreground">
                  {isOverdue ? `${Math.abs(daysUntilDue)} days overdue` : `${daysUntilDue} days left`}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_, dependency) => (
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedDependency(dependency);
              setShowDetailModal(true);
            }}
            className="text-muted-foreground hover:text-foreground"
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedDependency(dependency);
              setShowEditModal(true);
            }}
            className="text-muted-foreground hover:text-foreground"
          >
            <Edit className="w-4 h-4" />
          </Button>
          
          {/* Quick Status Actions */}
          {dependency.status === DependencyStatus.OPEN && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleStatusChange(dependency, DependencyStatus.IN_PROGRESS)}
              className="text-accent-blue hover:text-blue-300"
              title="Mark as In Progress"
            >
              <Clock className="w-4 h-4" />
            </Button>
          )}
          
          {(dependency.status === DependencyStatus.OPEN || dependency.status === DependencyStatus.IN_PROGRESS) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleStatusChange(dependency, DependencyStatus.RESOLVED)}
              className="text-status-success hover:text-green-300"
              title="Mark as Resolved"
            >
              <CheckCircle className="w-4 h-4" />
            </Button>
          )}
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedDependency(dependency);
              setShowDeleteConfirm(true);
            }}
            className="text-status-error hover:text-status-error/80"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">
            {viewMode === 'all' ? 'All Dependencies' : 'Dependencies at Risk'}
          </h3>
          <div className="text-sm text-muted-foreground">
            {dependencies.length} {dependencies.length === 1 ? 'dependency' : 'dependencies'}
          </div>
        </div>

        <div className="max-h-[calc(100vh-350px)] overflow-auto">
          <DataTable
            data={dependencies}
            columns={columns}
            searchable
            searchPlaceholder="Search dependencies..."
            emptyMessage={
              viewMode === 'all'
                ? "No dependencies found. Create your first dependency to track blocking relationships."
                : "No dependencies at risk. All critical and high severity dependencies are on track."
            }
          />
        </div>
      </div>

      {/* Edit Modal */}
      <GlassModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedDependency(null);
        }}
        title="Edit Dependency"
        subtitle="Update dependency details"
        size="lg"
      >
        {selectedDependency && (
          <DependencyForm
            dependency={selectedDependency}
            onSuccess={() => {
              setShowEditModal(false);
              setSelectedDependency(null);
              if (viewMode === 'all') {
                dispatch(fetchDependencies());
              } else {
                dispatch(fetchAtRiskDependencies());
              }
            }}
            onCancel={() => {
              setShowEditModal(false);
              setSelectedDependency(null);
            }}
          />
        )}
      </GlassModal>

      {/* Detail Modal */}
      <GlassModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedDependency(null);
        }}
        title="Dependency Details"
        subtitle="View complete dependency information"
        size="lg"
      >
        {selectedDependency && (
          <DependencyDetailModal
            dependency={selectedDependency}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedDependency(null);
            }}
          />
        )}
      </GlassModal>

      {/* Delete Confirmation Modal */}
      <GlassModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setSelectedDependency(null);
        }}
        title="Delete Dependency"
        subtitle="This action cannot be undone"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-muted-foreground">
            Are you sure you want to delete the dependency "{selectedDependency?.name}"?
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteConfirm(false);
                setSelectedDependency(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
            >
              Delete
            </Button>
          </div>
        </div>
      </GlassModal>
    </>
  );
};